/**
 * ObservationEvidenceTables — the read-only evidence summary shown at the top of
 * every Behavioural Observation review page and on Autism MDT Review.
 *
 * It renders two things:
 *   1. A DSM criteria summary table. Prefer the criteria-level
 *      `criteriaEvidence` map (one EvidencePill per criterion — SSI-like,
 *      without a childhood/adulthood split). Fall back to the legacy
 *      "DSM Criteria × Evidence" task-count matrix when that map is empty
 *      (older assessments that only have per-task decisions).
 *
 *      In the criteria-level table, a magnifying-glass icon appears next to
 *      each evidence pill when supporting data exists (tagged notes from Task
 *      Administration / ADOS-informed Coding, or free-text notes from Criterion
 *      Mapping). Clicking opens a detail popup — the OBS equivalent of the SSI
 *      Review popup that shows probing questions, quotes, and notes.
 *
 *   2. An optional "ADOS-Informed Coding" summary when ADOS scores exist.
 *      Default is a "View ADOS-informed coding" button that opens a dismissable
 *      scrollable dialog (OBS Review and MDT Review). Pass
 *      `adosPresentation="inline"` only if a layout still needs scores stacked
 *      under the DSM table. MDT also passes `compact` so the DSM table fits
 *      the half-column; OBS Review stays roomy.
 *
 * This is a pure, presentational, read-only component — it derives everything
 * from the props it is given and owns only the transient popup state.
 *
 * Use `ObservationEmptyState` (also exported here) when no tasks are selected.
 */

import React, { useState, useMemo } from 'react';
import { SearchIcon, XIcon } from '@heroicons/react/solid';
import { DSM5_DOMAINS, DSM5_EVIDENCE_OPTIONS } from '../config/observationTasksConfig';
import { ADOS_ALL_ITEMS, ADOS_DOMAINS } from '../config/adosConfig';
import { ADOS_SCORE_PILL_WIDTH_CLASS } from './AdosScoringPanel';
import { isObservationTaskAdministered } from '../../../../utils/observationTaskAdministration';
import {
  AUTISM_EVIDENCE_COLUMN_STYLES,
  AUTISM_EVIDENCE_PILL_COLOURS,
} from '../../../../constants/autismEvidenceColors';
import { hasAnyCriteriaEvidenceDecision } from '../../../../utils/observationCriteriaCompletion';
import { collectObsTaggedNotes } from '../utils/collectObsTaggedNotes';
import { renderNoteWithQuotes } from '../../../../utils/highlightQuotes';

/** ADOS scoring labels — mirror the scoring options used in the coding panel. */
const ADOS_SCORE_LABELS = [
  { value: 0, label: 'No impairment',          bg: 'bg-emerald-600', text: 'text-white' },
  { value: 1, label: 'Mild impairment',        bg: 'bg-amber-500',   text: 'text-white' },
  { value: 2, label: 'Significant impairment', bg: 'bg-red-500',     text: 'text-white' },
  { value: 3, label: 'Not applicable',         bg: 'bg-gray-500',    text: 'text-white' },
];

/**
 * Evidence column header — splits multi-word labels onto two lines
 * ("Clear evidence" → Clear / evidence) so narrow layouts (MDT half-column)
 * keep every header fully visible without cropping "Inconclusive".
 */
function EvidenceColumnHeader({ label }) {
  const spaceIdx = label.indexOf(' ');
  if (spaceIdx === -1) {
    return label;
  }
  return (
    <>
      {label.slice(0, spaceIdx)}
      <br />
      {label.slice(spaceIdx + 1)}
    </>
  );
}

/**
 * Per-evidence-level colour scheme for the column headers and populated cells.
 * Clear = red / No evidence = green — shared Autism scale.
 */
const EVIDENCE_COLUMN_STYLES = AUTISM_EVIDENCE_COLUMN_STYLES;

/** Compact filled pill — shared Autism scale (Clear = red, No evidence = green). */
function EvidencePill({ value }) {
  if (!value) return <span className="text-gray-400 text-xs">&mdash;</span>;
  const style = AUTISM_EVIDENCE_PILL_COLOURS[value]
    || { label: value, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block w-[7.5rem] text-center px-2.5 py-0.5 rounded-lg text-[11px] font-semibold ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

/**
 * Empty state shown when the clinician has not selected any observation tasks.
 * Exported so review pages can early-return it before rendering any review UI.
 */
export function ObservationEmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center px-8">
      <div className="w-20 h-20 bg-primary/5 rounded-2xl flex items-center justify-center mb-6">
        <svg className="w-10 h-10 text-primary/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      </div>
      <h2 className="text-gray-900 text-xl font-bold mb-2">No tasks selected</h2>
      <p className="text-gray-500 text-sm max-w-md">
        Select observation tasks to begin recording evidence.
        This review will aggregate your findings across all tasks by symptom.
      </p>
    </div>
  );
}

/**
 * Read-only ADOS score summary grouped by Module 4 domain. Shared by the
 * dialog and the leftover inline layout so both stay in sync.
 *
 * Each scored item shows its traffic-light score pill and, when the clinician
 * recorded notes during ADOS-informed Coding, a bulleted list of those notes
 * underneath — giving quick visibility into the rationale behind each score.
 */
function AdosCodingSummary({ adosData }) {
  const domainGroups = useMemo(() => {
    const groups = {};
    for (const item of ADOS_ALL_ITEMS) {
      if (!groups[item.domain]) groups[item.domain] = [];
      groups[item.domain].push(item);
    }
    return groups;
  }, []);

  return (
    <>
      {Object.entries(domainGroups).map(([domainKey, items]) => {
        const scoredItems = items.filter(item => adosData[`ados_${item.id}_score`] != null);
        if (scoredItems.length === 0) return null;
        return (
          <div key={domainKey} className="mb-4 last:mb-0">
            <h3 className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5">
              {ADOS_DOMAINS[domainKey] || domainKey}
            </h3>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-left border-collapse">
                <tbody>
                  {scoredItems.map((item) => {
                    const scoreValue = adosData[`ados_${item.id}_score`];
                    const scoreMeta = ADOS_SCORE_LABELS.find(s => s.value === scoreValue);
                    // Clinician notes recorded against this ADOS item during
                    // ADOS-informed Coding — an array of { id, text, ... }.
                    const rawNotes = adosData[`ados_${item.id}_notes`];
                    const notes = Array.isArray(rawNotes)
                      ? rawNotes.filter(n => n && String(n.text || '').trim())
                      : [];
                    return (
                      <tr key={item.id} className="border-b border-gray-200 last:border-b-0">
                        <td className="px-4 py-2 align-middle">
                          <p className="text-sm text-gray-900 font-semibold leading-snug">
                            {item.id}. {item.title}
                          </p>
                          {/* Bullet-point notes underneath the item title so the
                              clinician sees the rationale alongside the score. */}
                          {notes.length > 0 && (
                            <ul className="mt-1.5 space-y-1">
                              {notes.map((note) => (
                                <li key={note.id} className="flex items-start gap-2">
                                  <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                                  <span className="text-xs text-gray-600 leading-snug">{renderNoteWithQuotes(note.text)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-4 py-2 align-top text-right whitespace-nowrap w-[1%]">
                          {scoreMeta && (
                            <span className={`inline-flex items-center justify-center ${ADOS_SCORE_PILL_WIDTH_CLASS} px-2.5 py-0.5 rounded-lg text-xs font-semibold text-center leading-tight whitespace-nowrap ${scoreMeta.bg} ${scoreMeta.text}`}>
                              {scoreMeta.label}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default function ObservationEvidenceTables({
  tasks,
  selectedTaskIds,
  allTaskData,
  adosData = null,
  /**
   * Criteria-level evidence map from the Criterion Mapping redesign
   * (`{ A1: 'Clear evidence', ... }`). When any decision is present, the
   * summary renders as an SSI-style single-pill table instead of the legacy
   * per-task distribution matrix.
   */
  criteriaEvidence = null,
  /**
   * Free-text criterion notes from Criterion Mapping, keyed by criterion id
   * (`{ A1: [{ id, text }, ...], ... }`). Shown in the criteria-level detail
   * popup alongside tagged notes. Defaults to `{}` so the popup gracefully
   * omits the section when no data is passed (e.g. from MDT Review).
   */
  criteriaNotes = {},
  /**
   * How to show the ADOS summary when scores exist:
   *   'dialog' — button + modal (OBS Review and MDT Review)
   *   'inline' — stacked below the DSM table
   */
  adosPresentation = 'dialog',
  /**
   * Denser DSM table headers/cells for the MDT half-column. OBS Review uses
   * the same ADOS dialog but keeps the roomier table type.
   */
  compact = false,
}) {
  const useCriteriaLevelSummary = hasAnyCriteriaEvidenceDecision(criteriaEvidence);

  /**
   * Per-criterion supporting data for the criteria-level (v2) detail popup.
   * Collects tagged notes from Task Administration and ADOS-informed Coding
   * for each DSM criterion, plus any free-text criterion notes typed during
   * Criterion Mapping. Only computed when the criteria-level summary is active
   * — the legacy per-task matrix has its own popup pattern.
   *
   * Shape: { A1: { taskNotes, adosNotes, criterionNotes }, ... }
   */
  const perCriterionDetail = useMemo(() => {
    if (!useCriteriaLevelSummary) return {};
    const result = {};
    for (const domain of DSM5_DOMAINS) {
      const tagged = collectObsTaggedNotes({
        tagId: domain.id,
        selectedTaskIds,
        tasks,
        allTaskData: allTaskData || {},
        adosData: adosData || {},
      });
      const notes = Array.isArray(criteriaNotes?.[domain.id])
        ? criteriaNotes[domain.id].filter(n => n && String(n.text || '').trim())
        : [];
      result[domain.id] = {
        taskNotes: tagged.taskNotes,
        adosNotes: tagged.adosNotes,
        criterionNotes: notes,
      };
    }
    return result;
  }, [useCriteriaLevelSummary, selectedTaskIds, tasks, allTaskData, adosData, criteriaNotes]);

  // Attach the positional display number (index + 1) BEFORE filtering out
  // "Not administered" tasks, so excluding a middle task never renumbers the
  // ones after it — e.g. with tasks 1,2,3 and task 2 flagged, task 3 must still
  // read "3" here and everywhere else. Flagged tasks are dropped entirely so
  // they contribute to none of the evidence buckets.
  const selectedTasks = useMemo(
    () => selectedTaskIds
      .map((id, index) => ({ task: tasks.find(t => t.id === id), displayNumber: index + 1 }))
      .filter(({ task }) => task && isObservationTaskAdministered(allTaskData[task.id])),
    [selectedTaskIds, tasks, allTaskData],
  );

  /**
   * For each DSM-5 symptom, group the selected tasks by their evidence decision
   * into the four evidence buckets. Tasks with no decision for a symptom are
   * omitted — they haven't assessed that symptom yet.
   */
  const symptomRows = useMemo(() =>
    DSM5_DOMAINS.map(domain => {
      const buckets = {};
      DSM5_EVIDENCE_OPTIONS.forEach(opt => { buckets[opt] = []; });

      selectedTasks.forEach(({ task, displayNumber }) => {
        const data = allTaskData[task.id];
        if (!data) return;

        const extras = data.dsm_extraCriteria || [];
        const isRelevant =
          (task.relevantCriteria || []).includes(domain.id) || extras.includes(domain.id);
        if (!isRelevant) return;

        const evidence = data[`dsm_${domain.id}_evidence`];
        if (evidence && buckets[evidence]) {
          buckets[evidence].push({
            displayNumber,
            shortTitle: task.shortTitle,
          });
        }
      });

      // Inject Sensory (B4) evidence from the General Observations panel so it
      // appears in the review table alongside task-level evidence.
      if (domain.id === 'B4') {
        const sensoryData = allTaskData['general_sensory'];
        const sensoryEvidence = sensoryData?.dsm_B4_evidence;
        if (sensoryEvidence && buckets[sensoryEvidence]) {
          buckets[sensoryEvidence].push({
            displayNumber: '\u2605',
            shortTitle: 'Sensory Panel',
          });
        }
      }

      return { domain, buckets };
    }),
    [selectedTasks, allTaskData],
  );

  // Detail popup: { symptomLabel, evidenceLevel, entries } | null
  const [detailPopup, setDetailPopup] = useState(null);
  // MDT dialog for the full ADOS summary (kept separate from the task-detail popup).
  const [adosDialogOpen, setAdosDialogOpen] = useState(false);

  const hasAdos = adosData
    && Object.keys(adosData).some(k => k.endsWith('_score') && adosData[k] != null);

  const compactHeaders = compact;

  return (
    <>
      {/* ======== DSM Criteria summary ======== */}
      {/* Same max width as the Confidence / Referred For / Differentials /
          Formulation / Notes panels on Observation Review (`max-w-5xl`). The
          criteria-level table used to be `max-w-xl`, which left it narrower
          than those panels. On MDT Review the parent half-column is already
          narrower than 5xl, so this still fills that column. */}
      <div className="w-full max-w-5xl">
        <div className="rounded-2xl border border-gray-200 overflow-hidden">
          {useCriteriaLevelSummary ? (
            // SSI-like: one evidence pill per criterion (no childhood/adulthood).
            <table className="w-full text-left border-collapse">
              <colgroup>
                <col style={{ width: '55%' }} />
                <col style={{ width: '45%' }} />
              </colgroup>
              <thead>
                <tr className="bg-primary text-white">
                  <th className={`font-bold ${compactHeaders ? 'px-2 py-2 text-xs' : 'p-3 text-sm'}`}>
                    DSM Criteria
                  </th>
                  <th className={`font-bold text-center ${compactHeaders ? 'px-2 py-2 text-xs' : 'p-3 text-sm'}`}>
                    Evidence
                  </th>
                </tr>
              </thead>
              <tbody>
                {DSM5_DOMAINS.map((domain, i) => {
                  const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60';
                  const evidence = criteriaEvidence?.[domain.id] || null;
                  // Check whether this criterion has any supporting detail
                  // (tagged notes or free-text criterion notes) to show in the
                  // magnifying glass popup. When no detail exists the icon is
                  // hidden, matching the SSI pattern.
                  const detail = perCriterionDetail[domain.id];
                  const hasDetail = detail && (
                    detail.taskNotes.length > 0
                    || detail.adosNotes.length > 0
                    || detail.criterionNotes.length > 0
                  );
                  return (
                    <tr key={domain.id} className={`${bgClass} border-b border-gray-200 last:border-b-0`}>
                      <td className={`align-middle ${compactHeaders ? 'px-2 py-2' : 'p-3'}`}>
                        <p className={`text-gray-900 font-bold leading-snug ${compactHeaders ? 'text-xs' : 'text-sm'}`}>
                          {domain.id}. {domain.shortTitle}
                        </p>
                      </td>
                      <td className={`align-middle text-center ${compactHeaders ? 'px-2 py-2' : 'p-3'}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          <EvidencePill value={evidence} />
                          {hasDetail && (
                            <button
                              onClick={() => setDetailPopup({
                                type: 'criterion',
                                criterionId: domain.id,
                                shortTitle: domain.shortTitle,
                                evidence,
                                taskNotes: detail.taskNotes,
                                adosNotes: detail.adosNotes,
                                criterionNotes: detail.criterionNotes,
                              })}
                              className="text-gray-400 hover:text-primary transition-colors duration-150"
                              title="View details"
                            >
                              <SearchIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            // Legacy: task-count distribution across Clear / Partial / No / Inconclusive.
            <table className="w-full table-fixed text-left border-collapse">
              <colgroup>
                <col style={{ width: compactHeaders ? '30%' : '28%' }} />
                {DSM5_EVIDENCE_OPTIONS.map(opt => (
                  <col
                    key={opt}
                    style={{ width: `${(compactHeaders ? 70 : 72) / DSM5_EVIDENCE_OPTIONS.length}%` }}
                  />
                ))}
              </colgroup>

              <thead>
                <tr>
                  <th className={`bg-primary text-white font-bold ${compactHeaders ? 'px-2 py-2 text-xs' : 'p-3 text-sm'}`}>
                    DSM Criteria
                  </th>
                  {DSM5_EVIDENCE_OPTIONS.map(opt => {
                    const style = EVIDENCE_COLUMN_STYLES[opt];
                    return (
                      <th
                        key={opt}
                        className={`${style.headerBg} text-white font-bold text-center leading-tight ${
                          compactHeaders ? 'px-1 py-2 text-[11px]' : 'p-3 text-sm'
                        }`}
                      >
                        {compactHeaders ? <EvidenceColumnHeader label={opt} /> : opt}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {symptomRows.map((row, i) => {
                  const bgClass = i % 2 === 0 ? 'bg-white' : 'bg-gray-50/60';
                  return (
                    <tr key={row.domain.id} className={`${bgClass} border-b border-gray-200 last:border-b-0`}>
                      <td className={`align-top ${compactHeaders ? 'px-2 py-2' : 'p-3'}`}>
                        <p className={`text-gray-900 font-bold leading-snug ${compactHeaders ? 'text-xs' : 'text-sm'}`}>
                          {row.domain.id}. {row.domain.shortTitle}
                        </p>
                      </td>

                      {DSM5_EVIDENCE_OPTIONS.map(opt => {
                        const style = EVIDENCE_COLUMN_STYLES[opt];
                        const entries = row.buckets[opt];
                        const count = entries.length;
                        return (
                          <td
                            key={opt}
                            className={`align-middle text-center ${count > 0 ? style.cellBg : ''} ${
                              compactHeaders ? 'px-1 py-2' : 'p-3'
                            }`}
                          >
                            {count > 0 ? (
                              <div className={`flex items-center justify-center ${compactHeaders ? 'gap-1' : 'gap-1.5'}`}>
                                <span className={`font-semibold ${style.dot} ${compactHeaders ? 'text-[11px]' : 'text-xs'}`}>
                                  {count} {count === 1 ? 'task' : 'tasks'}
                                </span>
                                <button
                                  onClick={() => setDetailPopup({
                                    symptomLabel: `${row.domain.id}. ${row.domain.shortTitle}`,
                                    evidenceLevel: opt,
                                    entries,
                                  })}
                                  className="text-gray-400 hover:text-primary transition-colors duration-150"
                                  title="View tasks"
                                >
                                  <SearchIcon className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">&mdash;</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {hasAdos && adosPresentation === 'dialog' && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setAdosDialogOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-semibold hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              View ADOS-informed coding
            </button>
          </div>
        )}
      </div>

      {/* ======== ADOS-Informed Coding — inline fallback ======== */}
      {hasAdos && adosPresentation === 'inline' && (
        <div className="w-full max-w-5xl mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">ADOS-Informed Coding</h2>
          <AdosCodingSummary adosData={adosData} />
        </div>
      )}

      {/* ---- Task-detail popup (magnifying glass on legacy evidence cells) ---- */}
      {detailPopup && detailPopup.type !== 'criterion' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDetailPopup(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[420px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setDetailPopup(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            <h2 className="text-base font-bold text-gray-900 mb-0.5">
              {detailPopup.symptomLabel}
            </h2>
            <p className="text-xs text-gray-500 mb-4">{detailPopup.evidenceLevel}</p>

            <ul className="space-y-1">
              {detailPopup.entries.map(e => (
                <li key={e.displayNumber} className="flex items-start gap-2">
                  <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                  <span className="text-sm text-gray-700 leading-snug">
                    <span className="font-semibold">Task {e.displayNumber}.</span>{' '}
                    {e.shortTitle}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <style>{`
            @keyframes fadeScaleIn {
              from { opacity: 0; transform: scale(0.95); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* ---- Criterion-level detail popup (v2 criteria summary table) ----
           Mirrors the SSI Review detail popup layout: title, evidence subtitle,
           then up to three sections showing tagged ADOS notes, tagged Task Admin
           notes, and free-text criterion notes from Criterion Mapping. Each
           section only renders when it has content. ---- */}
      {detailPopup && detailPopup.type === 'criterion' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDetailPopup(null)}
          />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 w-[520px] max-w-[90vw] max-h-[80vh] overflow-y-auto text-left animate-[fadeScaleIn_0.15s_ease-out]">
            <button
              onClick={() => setDetailPopup(null)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>

            <h2 className="text-base font-bold text-gray-900 mb-1">
              {detailPopup.criterionId}. {detailPopup.shortTitle}
            </h2>
            <div className="mb-4">
              <EvidencePill value={detailPopup.evidence} />
            </div>

            {/* Two-level grouping: one heading per origin (Task Administration
                / ADOS-Informed Coding), then one subheading per task or ADOS
                item, then the notes as bullets. Quotes are highlighted inline
                to match the SSI popup. */}
            {(() => {
              const sections = [
                { key: 'task', heading: 'Task Administration', notes: detailPopup.taskNotes },
                { key: 'ados', heading: 'ADOS-Informed Coding', notes: detailPopup.adosNotes },
              ];

              return (
                <>
                  {sections.map((section) => {
                    if (!section.notes.length) return null;

                    // Group notes that share a source (same task / ADOS item)
                    // so the item name is shown once, not on every bullet.
                    const sourceOrder = [];
                    const sourceMap = {};
                    for (const note of section.notes) {
                      if (!sourceMap[note.sourceLabel]) {
                        sourceMap[note.sourceLabel] = [];
                        sourceOrder.push(note.sourceLabel);
                      }
                      sourceMap[note.sourceLabel].push(note);
                    }

                    return (
                      <div key={section.key} className="mb-4">
                        <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                          {section.heading}
                        </h3>
                        {sourceOrder.map((sourceLabel) => (
                          <div key={sourceLabel} className="mb-3 last:mb-0">
                            <p className="text-xs font-semibold text-gray-700 mb-1.5">{sourceLabel}</p>
                            <ul className="space-y-1.5 ml-0.5">
                              {sourceMap[sourceLabel].map((note) => (
                                <li key={note.id} className="flex items-start gap-2">
                                  <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                                  <span className="text-sm text-gray-700 leading-relaxed">
                                    {renderNoteWithQuotes(note.text)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    );
                  })}

                  {/* Free-text criterion notes from Criterion Mapping */}
                  {detailPopup.criterionNotes.length > 0 && (
                    <div className="mb-4">
                      <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-1.5">
                        Additional Criterion Notes
                      </h3>
                      <ul className="space-y-1.5">
                        {detailPopup.criterionNotes.map((note) => (
                          <li key={note.id} className="flex items-start gap-2">
                            <span className="text-gray-400 text-xs mt-0.5 flex-shrink-0">&bull;</span>
                            <span className="text-sm text-gray-700 leading-relaxed">{renderNoteWithQuotes(note.text)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <style>{`
            @keyframes fadeScaleIn {
              from { opacity: 0; transform: scale(0.95); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}

      {/* ---- ADOS summary dialog (OBS Review and MDT Review) ---- */}
      {adosDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setAdosDialogOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ados-coding-dialog-title"
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col text-left animate-[fadeScaleIn_0.15s_ease-out]"
          >
            <div className="flex items-start justify-between gap-3 px-6 pt-5 pb-3 border-b border-gray-100 flex-shrink-0">
              <h2 id="ados-coding-dialog-title" className="text-lg font-bold text-gray-900">
                ADOS-Informed Coding
              </h2>
              <button
                type="button"
                onClick={() => setAdosDialogOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors -mt-0.5"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto min-h-0">
              <AdosCodingSummary adosData={adosData} />
            </div>
          </div>

          <style>{`
            @keyframes fadeScaleIn {
              from { opacity: 0; transform: scale(0.95); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
